import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteDocument, downloadDocument, getDocumentsByClaimId, selectClaimDocuments, selectDocumentsLoading, selectDocumentsError, selectDocumentsMessage, selectLastRefresh } from '../../features/documents/documentSlice';
import { selectUserRoles } from '../../features/auth/authSlice';
import { LoadingButton } from '../ui/LoadingSpinner';
import { getDocumentTypeLabel, requiresStampValidation } from '../../features/documents/documentService';
import { getStampValidationStatus, canValidateDocument } from '../../utils/stampValidationUtils';
import DocumentPreview from './DocumentPreview';
import StampValidationModal from './StampValidationModal';
import { Link } from 'react-router-dom';
import OmButton from '../ui/OmButton';
import OmCircularProgress from '../ui/OmCircularProgress';
import React from 'react';

// Memoized DocumentItem component to prevent unnecessary re-renders
const DocumentItem = React.memo(({
  document,
  isClaimsOfficer,
  isApprover,
  showValidation,
  canDownload,
  canDelete,
  deleteConfirm,
  getFileIcon,
  getSafeDocumentTypeLabel,
  getMedicalDocumentIndicator,
  formatFileSize,
  formatDate,
  getStampValidationBadge,
  onPreview,
  onValidateStamp,
  onDownload,
  onDelete,
  onDeleteConfirm,
  onCancelDelete
}) => {
  return (
    <div className="bg-crystal-white border border-silver-mist/30 hover:border-fresh-leaf/40 rounded-lg transition-all duration-200 group hover:shadow-md">
      <div className="p-5 flex items-start justify-between">
        <div className="flex items-start space-x-4 flex-1 min-w-0">
          <div className="flex-shrink-0 relative">
            <div className="p-3 bg-gradient-to-br from-fresh-leaf/10 to-forest-fern/10 rounded-lg border border-fresh-leaf/20">
              {getFileIcon(document.mimeType, document.name)}
            </div>
            {requiresStampValidation(document.type) && (
              <div className="absolute -top-1 -right-1">
                <div className="w-3 h-3 bg-zesty-lime rounded-full border-2 border-crystal-white shadow-sm"></div>
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-bold text-deep-jungle truncate mb-3 group-hover:text-fresh-leaf transition-colors">
              {document.name || document.filename || 'Unnamed Document'}
            </h4>
            
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-fresh-leaf/10 text-fresh-leaf border border-fresh-leaf/20 shadow-sm">
                {getSafeDocumentTypeLabel(document)}
              </span>
              {getMedicalDocumentIndicator(document)}
            </div>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-steel">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-medium">{formatFileSize(document.size)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">{formatDate(document.createdAt || document.uploadedAt)}</span>
              </div>
            </div>
            
            {/* Stamp Validation Status */}
            {(isClaimsOfficer || isApprover) && showValidation && (
              <div className="mt-4 p-4 bg-gradient-to-r from-silver-mist/10 to-crystal-white/50 rounded-lg border border-silver-mist/20">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-deep-jungle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-semibold text-deep-jungle">Stamp Validation:</span>
                  </div>
                  {getStampValidationBadge(document)}
                  {document.stampValidatedBy && (
                    <div className="flex items-center space-x-1 text-sm text-steel">
                      <span>validated by</span>
                      <span className="font-medium text-deep-jungle">{document.stampValidatedBy.username}</span>
                    </div>
                  )}
                </div>
                {document.stampValidationNotes && (
                  <div className="bg-crystal-white/60 border-l-4 border-fresh-leaf/30 pl-4 py-2 rounded-r-lg">
                    <p className="text-sm text-graphite italic">
                      "{document.stampValidationNotes}"
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 ml-6">
          {/* Preview Button */}
          <OmButton
            onClick={() => onPreview(document)}
            buttonType="outlined-button"
            buttonSize="small"
            title="Preview document"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </OmButton>

          {/* Validate Stamp Button */}
          {canValidateDocument(document, isClaimsOfficer, isApprover) && getStampValidationStatus(document) !== 'VALID' && (
            <OmButton
              onClick={() => onValidateStamp(document)}
              buttonType="outlined-button"
              buttonSize="small"
              title="Validate hospital stamp"
            >
              Validate
            </OmButton>
          )}

          {/* Download Button */}
          {canDownload && (
            <OmButton
              onClick={() => onDownload(document.id, document.name)}
              buttonType="outline-button"
              buttonSize="small"
              title="Download document"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </OmButton>
          )}

          {/* Delete Button */}
          {canDelete && (
            <div className="flex items-center gap-2">
              {deleteConfirm === document.id ? (
                <div className="flex gap-2">
                  <OmButton
                    onClick={() => onDelete(document.id)}
                    buttonType="filled-button"
                    buttonSize="small"
                    title="Confirm delete"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Confirm
                  </OmButton>
                  <OmButton
                    onClick={onCancelDelete}
                    buttonType="outline-button"
                    buttonSize="small"
                    title="Cancel delete"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cancel
                  </OmButton>
                </div>
              ) : (
                <OmButton
                  onClick={() => onDeleteConfirm(document.id)}
                  buttonType="outline-button"
                  buttonSize="small"
                  title="Delete document"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </OmButton>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.document.id === nextProps.document.id &&
    prevProps.document.name === nextProps.document.name &&
    prevProps.document.filename === nextProps.document.filename &&
    prevProps.document.mimeType === nextProps.document.mimeType &&
    prevProps.document.size === nextProps.document.size &&
    prevProps.document.type === nextProps.document.type &&
    prevProps.document.createdAt === nextProps.document.createdAt &&
    prevProps.document.uploadedAt === nextProps.document.uploadedAt &&
    prevProps.document.stampValid === nextProps.document.stampValid &&
    prevProps.document.stampValidatedAt === nextProps.document.stampValidatedAt &&
    prevProps.document.stampValidationNotes === nextProps.document.stampValidationNotes &&
    JSON.stringify(prevProps.document.stampValidatedBy) === JSON.stringify(nextProps.document.stampValidatedBy) &&
    prevProps.isClaimsOfficer === nextProps.isClaimsOfficer &&
    prevProps.isApprover === nextProps.isApprover &&
    prevProps.showValidation === nextProps.showValidation &&
    prevProps.canDownload === nextProps.canDownload &&
    prevProps.canDelete === nextProps.canDelete &&
    prevProps.deleteConfirm === nextProps.deleteConfirm
  );
});

DocumentItem.displayName = 'DocumentItem';

function DocumentList({ claimId, canDelete = false, canDownload = true, showValidation = false }) {
  const dispatch = useDispatch();
  const documents = useSelector(selectClaimDocuments);
  const isLoading = useSelector(selectDocumentsLoading);
  const isError = useSelector(selectDocumentsError);
  const message = useSelector(selectDocumentsMessage);
  const lastRefresh = useSelector(selectLastRefresh);
  const userRoles = useSelector(selectUserRoles);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [validationDocument, setValidationDocument] = useState(null);
  
  // Validation status filter state (handled separately due to custom logic)
  const [filterValidationStatus, setFilterValidationStatus] = useState('ALL');

  // Check if user is Claims Officer
  const isClaimsOfficer = userRoles.includes('Claims_Officer');
  const isApprover = userRoles.includes('Approver');

  // Fetch documents when component mounts or claimId changes
  useEffect(() => {
    async function fetchDocuments() {
      try {
        if (claimId) {
          await dispatch(getDocumentsByClaimId(claimId));
        }
      } catch (err) {
        console.error('Failed to fetch claim documents:', err);
      }
    }
    fetchDocuments();
  }, [dispatch, claimId, lastRefresh]);

  // Get stamp validation status badge
  const getStampValidationBadge = (document) => {
    if (!requiresStampValidation(document.type)) {
      return (
        <span className="badge inline-flex items-center bg-steel/10 text-graphite border border-steel/20">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Not Required
        </span>
      );
    }

    const status = getStampValidationStatus(document);
    switch (status) {
      case 'VALID':
        return (
          <span className="badge badge-success inline-flex items-center">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Valid Stamp
          </span>
        );
      case 'INVALID':
        return (
          <span className="badge badge-error inline-flex items-center">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Invalid Stamp
          </span>
        );
      case 'PENDING':
      default:
        return (
          <span className="badge badge-pending inline-flex items-center">
            <div className="mr-1">
              <OmCircularProgress
                indeterminate={true}
                size="small"
              />
            </div>
            Pending Review
          </span>
        );
    }
  };

  // Filter documents based on validation status
  const getFilteredDocuments = () => {
    let filteredDocs = documents || [];

    if (filterValidationStatus !== 'ALL') {
      filteredDocs = filteredDocs.filter(doc => {
        if (filterValidationStatus === 'REQUIRES_VALIDATION') {
          return requiresStampValidation(doc.type);
        }
        if (filterValidationStatus === 'PENDING') {
          return requiresStampValidation(doc.type) && getStampValidationStatus(doc) === 'PENDING';
        }
        if (filterValidationStatus === 'VALID') {
          return requiresStampValidation(doc.type) && getStampValidationStatus(doc) === 'VALID';
        }
        if (filterValidationStatus === 'INVALID') {
          return requiresStampValidation(doc.type) && getStampValidationStatus(doc) === 'INVALID';
        }
        return true;
      });
    }

    return filteredDocs;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get file icon based on type
  const getFileIcon = (mimeType, fileName) => {
    if (mimeType?.includes('pdf') || fileName?.toLowerCase().endsWith('.pdf')) {
      return (
        <div className="w-10 h-10 bg-error/10 border border-error/20 flex items-center justify-center">
          <svg className="h-6 w-6 text-error" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
    if (mimeType?.includes('image')) {
      return (
        <div className="w-10 h-10 bg-info/10 border border-info/20 flex items-center justify-center">
          <svg className="h-6 w-6 text-info" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
    if (mimeType?.includes('word') || fileName?.toLowerCase().includes('.doc')) {
      return (
        <div className="w-10 h-10 bg-info/10 border border-info/20 flex items-center justify-center">
          <svg className="h-6 w-6 text-info" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
    if (mimeType?.includes('excel') || fileName?.toLowerCase().includes('.xls')) {
      return (
        <div className="w-10 h-10 bg-fresh-leaf/10 border border-fresh-leaf/20 flex items-center justify-center">
          <svg className="h-6 w-6 text-forest-fern" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
    // Default file icon
    return (
      <div className="w-10 h-10 bg-steel/10 border border-steel/20 flex items-center justify-center">
        <svg className="h-6 w-6 text-steel" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      </div>
    );
  };

  // Handle download
  const handleDownload = (documentId, filename) => {
    dispatch(downloadDocument({ 
      documentId, 
      filename: filename || `document-${documentId}` 
    }));
  };

  // Handle delete
  const handleDelete = (documentId) => {
    if (deleteConfirm === documentId) {
      dispatch(deleteDocument(documentId));
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(documentId);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  // Handle preview
  const handlePreview = (document) => {
    setPreviewDocument(document);
  };

  // Close preview
  const closePreview = () => {
    setPreviewDocument(null);
  };

  // Handle stamp validation
  const handleValidateStamp = (document) => {
    setValidationDocument(document);
  };

  // Close validation modal
  const closeValidation = () => {
    setValidationDocument(null);
  };

  // Handle validation success
  const handleValidationSuccess = () => {
    setValidationDocument(null);
    // Documents are updated automatically via Redux state
  };

  // Handle bulk validation
  const handleBulkValidation = () => {
    const pendingMedicalDocs = filteredDocuments.filter(doc => 
      requiresStampValidation(doc.type) && 
      getStampValidationStatus(doc) === 'PENDING'
    );
    
    if (pendingMedicalDocs.length > 0) {
      setValidationDocument(pendingMedicalDocs[0]); // Start with first pending document
    }
  };

  // Get medical verification summary
  const getMedicalVerificationSummary = () => {
    const safeDocuments = Array.isArray(documents) ? documents : [];
    const medicalDocs = safeDocuments.filter(doc => requiresStampValidation(doc.type));
    if (medicalDocs.length === 0) return null;

    const valid = medicalDocs.filter(doc => getStampValidationStatus(doc) === 'VALID').length;
    const invalid = medicalDocs.filter(doc => getStampValidationStatus(doc) === 'INVALID').length;
    const pending = medicalDocs.filter(doc => getStampValidationStatus(doc) === 'PENDING').length;
    const total = medicalDocs.length;
    const completionPercentage = Math.round((valid / total) * 100);

    return { valid, invalid, pending, total, completionPercentage };
  };

  const medicalSummary = getMedicalVerificationSummary();

  // Helper function to get document type with fallbacks
  const getDocumentType = (document) => {
    const docType = document.type;
    if (docType) {
      return docType;
    }
    
    const filename = document.name || document.filename || '';
    if (!filename) {
      return 'OTHER';
    }
    const lowerFilename = filename.toLowerCase();
    
    if (lowerFilename.includes('death') || lowerFilename.includes('certificate')) {
      return 'DEATH_CERTIFICATE';
    }
    if (lowerFilename.includes('medical') || lowerFilename.includes('report')) {
      return 'MEDICAL_REPORT';
    }
    if (lowerFilename.includes('bank') || lowerFilename.includes('statement')) {
      return 'BANK_STATEMENT';
    }
    if (lowerFilename.includes('id') || lowerFilename.includes('identity')) {
      return 'ID_DOCUMENT';
    }
    if (lowerFilename.includes('policy')) {
      return 'POLICY_DOCUMENT';
    }
    
    return 'OTHER';
  };

  // Safe function to get document type label
  const getSafeDocumentTypeLabel = (document) => {
    try {
      const docType = getDocumentType(document);
      const label = getDocumentTypeLabel(docType);
      return label || 'Document';
    } catch {
      // Console log removed for production
      return 'Document';
    }
  };

  // Get medical document indicator
  const getMedicalDocumentIndicator = (document) => {
    if (!requiresStampValidation(document.type)) {
      return null;
    }

    return (
      <omk-tag type="information" size="small" title="Requires medical stamp validation">
        <svg className="w-3 h-3 mr-1 inline-block" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
        Medical
      </omk-tag>
    );
  };

  const filteredDocuments = getFilteredDocuments();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <OmCircularProgress
          indeterminate={true}
          size="medium"
        />
        <span className="text-steel font-medium ml-3">Loading documents...</span>
      </div>
    );
  }

  if (isError && message) {
    return (
      <div className="alert alert-error bg-error/5 border-l-4 border-error p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-error" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-error font-medium">{message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-6 bg-steel/10 border border-steel/20 flex items-center justify-center">
          <svg className="h-8 w-8 text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-deep-jungle mb-2">No documents found</h3>
        <p className="text-steel mb-8 max-w-md mx-auto">No documents have been uploaded for this claim yet. Upload supporting documents to proceed with claim processing.</p>
        
        {canDelete && (
          <Link
            to={`/claims/${claimId}/documents`}
            className="btn-primary inline-flex items-center"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            Upload Documents
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-deep-jungle">
            Documents
          </h3>
          <p className="text-steel text-sm mt-1">{documents.length} document{documents.length !== 1 ? 's' : ''} uploaded</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Medical Verification Summary */}
          {(isClaimsOfficer || isApprover) && showValidation && medicalSummary && (
            <div className="flex items-center space-x-4">
              <div className="text-sm text-steel">
                Medical Verification: <span className="font-medium text-deep-jungle">{medicalSummary.valid}/{medicalSummary.total}</span>
              </div>
              <div className="w-32 h-2 bg-steel/20 border border-steel/30">
                <div 
                  className="bg-fresh-leaf h-full transition-all duration-500" 
                  style={{ width: `${medicalSummary.completionPercentage}%` }}
                ></div>
              </div>
              <span className="text-sm font-bold text-deep-jungle min-w-[3rem]">
                {medicalSummary.completionPercentage}%
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Bulk Validation Button */}
            {isClaimsOfficer && showValidation && medicalSummary && medicalSummary.pending > 0 && (
              <button
                onClick={handleBulkValidation}
                className="btn-secondary text-sm inline-flex items-center px-4 py-2"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Validate Medical Docs
              </button>
            )}
            
            {/* Validation Filter */}
            {(isClaimsOfficer || isApprover) && showValidation && (
              <div className="flex items-center gap-2">
                <label htmlFor="validation-filter" className="text-sm font-medium text-steel whitespace-nowrap">
                  Filter:
                </label>
                <select
                  id="validation-filter"
                  value={filterValidationStatus}
                  onChange={(e) => setFilterValidationStatus(e.target.value)}
                  className="border border-woodland-moss/30 focus:border-fresh-leaf focus:ring-2 focus:ring-woodland-moss/20 text-sm px-3 py-2 bg-crystal-white text-deep-jungle min-w-[160px]"
                >
                  <option value="ALL">All Documents</option>
                  <option value="REQUIRES_VALIDATION">Medical Only</option>
                  <option value="PENDING">Pending Validation</option>
                  <option value="VALID">Valid Stamps</option>
                  <option value="INVALID">Invalid Stamps</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Alerts */}
      {(isClaimsOfficer || isApprover) && showValidation && medicalSummary && medicalSummary.invalid > 0 && (
        <div className="alert bg-error/5 border-l-4 border-error p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-error" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-bold text-error">
                Invalid Medical Stamps Detected
              </h3>
              <p className="mt-1 text-sm text-error">
                {medicalSummary.invalid} medical document(s) have invalid stamps. Claim approval is blocked until all medical documents have valid stamps.
              </p>
            </div>
          </div>
        </div>
      )}

      {medicalSummary && medicalSummary.pending > 0 && (isClaimsOfficer || isApprover) && showValidation && (
        <div className="alert bg-warning/5 border-l-4 border-warning p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-warning" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-bold text-warning">
                Medical Verification Pending
              </h3>
              <p className="mt-1 text-sm text-warning">
                {medicalSummary.pending} medical document(s) require stamp validation before claim approval.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Documents Grid */}
      <div className="space-y-3">
        {filteredDocuments.map((document) => (
          <DocumentItem
            key={document.id}
            document={document}
            isClaimsOfficer={isClaimsOfficer}
            isApprover={isApprover}
            showValidation={showValidation}
            canDownload={canDownload}
            canDelete={canDelete}
            deleteConfirm={deleteConfirm}
            getFileIcon={getFileIcon}
            getSafeDocumentTypeLabel={getSafeDocumentTypeLabel}
            getMedicalDocumentIndicator={getMedicalDocumentIndicator}
            formatFileSize={formatFileSize}
            formatDate={formatDate}
            getStampValidationBadge={getStampValidationBadge}
            onPreview={handlePreview}
            onValidateStamp={handleValidateStamp}
            onDownload={handleDownload}
            onDelete={handleDelete}
            onDeleteConfirm={setDeleteConfirm}
            onCancelDelete={cancelDelete}
          />
        ))}
      </div>

      {/* Document Preview Modal */}
      {previewDocument && (
        <DocumentPreview
          document={previewDocument}
          onClose={closePreview}
          showStampValidation={isClaimsOfficer}
          onValidationSuccess={handleValidationSuccess}
        />
      )}

      {/* Stamp Validation Modal */}
      {validationDocument && (
        <StampValidationModal
          document={validationDocument}
          onClose={closeValidation}
          onSuccess={handleValidationSuccess}
        />
      )}
    </div>
  );
}

export default DocumentList;
